import type React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export interface Roaster {
  roasterName: string;
}

export type ConnectedPlatform = "shopify" | "woocommerce";

interface AuthContextType {
  roaster: Roaster;
  isAuthenticated: boolean;
  isOnboarded: boolean;
  isAdmin: boolean;
  connectedPlatform: ConnectedPlatform | null;
  completeOnboarding: (platform: ConnectedPlatform) => void;
  clearState?: () => void;
  logout: () => void;
}

const defaultRoaster: Roaster = {
  roasterName: "Demo Roaster",
};

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Sample data for Storybook stories
export const sampleAuthContext: AuthContextType = {
  roaster: {
    roasterName: "Premium Coffee Roasters",
  },
  isAuthenticated: true,
  isOnboarded: true,
  isAdmin: false,
  connectedPlatform: "shopify",
  completeOnboarding: () => {},
  logout: () => {},
};

interface AuthProviderProps {
  readonly children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [roaster, setRoaster] = useState<Roaster>(defaultRoaster);
  const [isAuthenticated, setIsAuthenticated] = useState(true); // Default to true for demo
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [connectedPlatform, setConnectedPlatform] = useState<ConnectedPlatform | null>(null);

  useEffect(() => {
    // Check localStorage for saved auth state
    const savedOnboarded = localStorage.getItem("isOnboarded");
    const savedPlatform = localStorage.getItem("connectedPlatform") as ConnectedPlatform | null;
    const savedIsAdmin = localStorage.getItem("isAdmin");

    if (savedOnboarded === "true") {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- restore persisted onboarding state from localStorage on mount
      setIsOnboarded(true);
    }
    if (savedPlatform) {
      setConnectedPlatform(savedPlatform);
    }
    if (savedIsAdmin === "true") {
      setIsAdmin(true);
    }

    // In a real app, fetch roaster info from API
    const savedRoasterName = localStorage.getItem("roasterName");
    if (savedRoasterName) {
      setRoaster({ roasterName: savedRoasterName });
    }
  }, []);

  const completeOnboarding = useCallback((platform: ConnectedPlatform) => {
    setIsOnboarded(true);
    setConnectedPlatform(platform);
    localStorage.setItem("isOnboarded", "true");
    localStorage.setItem("connectedPlatform", platform);
  }, []);

  const clearState = useCallback(() => {
    setRoaster(defaultRoaster);
    setIsOnboarded(false);
    setIsAdmin(false);
    setConnectedPlatform(null);
    ["isOnboarded", "connectedPlatform", "isAdmin", "roasterName"].forEach((key) => localStorage.removeItem(key));
  }, []);

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    clearState();
  }, [clearState]);

  const value = useMemo<AuthContextType>(
    () => ({
      roaster,
      isAuthenticated,
      isOnboarded,
      isAdmin,
      connectedPlatform,
      completeOnboarding,
      clearState,
      logout,
    }),
    [connectedPlatform, completeOnboarding, clearState, isAdmin, isAuthenticated, isOnboarded, logout, roaster]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// Mock provider for Storybook stories
export interface MockAuthProviderProps {
  readonly children: React.ReactNode;
  readonly roaster?: Roaster;
  readonly isAuthenticated?: boolean;
  readonly isOnboarded?: boolean;
  readonly isAdmin?: boolean;
  readonly connectedPlatform?: ConnectedPlatform | null;
}

export const mockAuthDefaults: Omit<AuthContextType, "completeOnboarding" | "logout"> = {
  roaster: {
    roasterName: "Premium Coffee Roasters",
  },
  isAuthenticated: true,
  isOnboarded: true,
  isAdmin: false,
  connectedPlatform: "shopify",
};

export function MockAuthProvider({
  children,
  roaster = mockAuthDefaults.roaster,
  isAuthenticated = mockAuthDefaults.isAuthenticated,
  isOnboarded = mockAuthDefaults.isOnboarded,
  isAdmin = mockAuthDefaults.isAdmin,
  connectedPlatform = mockAuthDefaults.connectedPlatform,
}: MockAuthProviderProps) {
  const value = useMemo<AuthContextType>(
    () => ({
      roaster,
      isAuthenticated,
      isOnboarded,
      isAdmin,
      connectedPlatform,
      completeOnboarding: () => {},
      logout: () => {},
    }),
    [connectedPlatform, isAdmin, isAuthenticated, isOnboarded, roaster]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
